import React from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

const Modals = ({
  isOpen,
  onClose,
  children,
  width = "md",
  title = null,
  showCloseButton = true,
  footer = null,
  maxHeight = "80vh",
  padding = "p-6",
  contentPadding = "px-1 py-2",
  className = "",
  variant = "default",
}) => {
  // Width sizing map
  const sizeMap = {
    xs: "max-w-xs",
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
    "6xl": "max-w-6xl",
    "7xl": "max-w-7xl",
    24: "max-w-xs",
    32: "max-w-sm",
    36: "max-w-md",
    42: "max-w-lg",
    48: "max-w-xl",
    56: "max-w-2xl",
    64: "max-w-3xl",
    72: "max-w-4xl",
    80: "max-w-5xl",
    96: "max-w-6xl",
  };

  // Determine width class
  const widthClass =
    sizeMap[width] || (width.includes("max-w-") ? width : "max-w-lg");

  // Footer style based on variant
  const footerClass =
    variant === "accent"
      ? "border-t border-green-600/20 bg-gradient-to-r from-[#57A863]/10 to-[#3d9448]/10"
      : "border-t border-gray-200 bg-gray-50";

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* Backdrop with proper transition */}
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      {/* Container */}
      <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          {/* Modal Panel */}
          <DialogPanel
            transition
            className={`relative w-full ${widthClass} transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95 ${className}`}
          >
            {/* Header (conditional) */}
            {(title || showCloseButton) && (
              <div className="px-6 py-4 bg-gradient-to-r from-[#57A863] to-[#3d9448] text-white">
                {title && (
                  <Dialog.Title
                    className={`text-lg font-semibold ${
                      variant === "accent" ? "text-white" : "text-white"
                    }`}
                  >
                    {title}
                  </Dialog.Title>
                )}

                {/* Close button */}
                {showCloseButton && (
                  <button
                    onClick={onClose}
                    className={`absolute right-4 top-4 rounded-full p-1 ${
                      variant === "accent"
                        ? "text-white/80 hover:bg-white/10 hover:text-white"
                        : "text-gray-400 hover:bg-gray-100 hover:text-gray-500"
                    } focus:outline-none focus:ring-2 focus:ring-white/50`}
                    aria-label="Fermer"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            )}

            {/* Content area with customizable padding and max height */}
            <div
              className={`${padding} ${
                !title && !showCloseButton ? "pt-8" : ""
              }`}
              style={{ maxHeight: maxHeight, overflowY: "auto" }}
            >
              <div className={contentPadding}>{children}</div>
            </div>

            {/* Footer (conditional) */}
            {footer && (
              <div className={`px-6 py-4 ${footerClass}`}>{footer}</div>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default Modals;
